import { Form } from '@ant-design/compatible';
import { Col, Row, Tabs } from 'antd';
import PermissionCheck from 'common/features/permission-check';
import SidePanel from 'common/side-panel/side-panel';
import TranslationModalContainer from 'common/translate/containers/translation-modal-container';
import NavContextItem from 'order/create/components/nav-context-item';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { isToggleEnabled } from 'react-awesome-feature-toggles/dist/feature-toggles';
import { injectIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { Button, notification } from 'ui';
import { withRouter } from 'utils/withRouter';
import { ticketActionStatus } from '../../../utils/app-configs';
import { sanitizeNote } from '../../../utils/helpers';
import useConfirmationMessage from '../../../utils/hooks/useConfirmationMessage';
import GeneralTab from './tab-panes/general-tab';
import OperationsTableTab from './tab-panes/operations-table-tab';
import PartTableTab from './tab-panes/part-table-tab';
import { tabs } from './tab-panes/tab-helpers';
// eslint-disable-next-line max-len
import { resetSmartActionSuccess } from '../../containers/smart-action-sidepanel/smart-action-sidepanel-actions';

const { TabPane } = Tabs;

export const StyledTabs = styled(Tabs)`
  .ant-tabs-nav {
    font-size: 15px;
    text-transform: uppercase;
    font-weight: bold;
  }
  .ant-tabs-tab {
    font-weight: 400;
    font-size: 16px;
    text-transform: uppercase;
    width: 110px;
    justify-content: center;
  }

  .ant-tabs-ink-bar {
    background: ${p => p.theme.colors.theme} !important;
  }

  .ant-tabs-tab-active {
    .ant-tabs-tab-btn {
      color: ${p => p.theme.colors.theme} !important;
    }
  }
`;

const StyledSaveButton = styled(Button)`
  margin-top: 30px;
  text-transform: uppercase;
`;

const Divider = styled.div`
  border-top: 1px solid #dce4ea;
  margin: 10px 0;
`;

const StyledButton = styled(Button)`
  background-color: ${p => p.theme.colors.theme} !important;
  width: 100%;
  text-transform: uppercase;
`;

const StyledButtonCol = styled(Col)`
  position: absolute;
  bottom: 0;
  right: 0;
`;

const HeaderRow = styled(Row)`
  position: relative;
  min-height: 35px;
`;

const StyledTabPane = styled(TabPane)`
  .comment-text > .ant-legacy-form-item-label {
    width: 100%;
  }
`;

const Counter = styled.div`
  margin-left: 5px;
  background: ${p => p.theme.colors.theme};
  display: inline-block;
  font-size: 9px;
  padding: 0 4px;
  min-width: 17px;
  border-radius: 8px;
  text-align: center;
  color: white;
`;

const SmartActionSidePanel = ({
  id,
  match,
  history,
  intl,
  context,
  form,
  ticket,
  action,
  tags,
  saveSmartAction,
  createSmartAction,
  success,
  isSaving,
  setActionForCreatingOrder,
  fetchSmartAction,
  isLoadingAction,
  assignOrderToSmartAction = null,
  origin,
  openTicket,
}) => {
  const dispatch = useDispatch();
  const [activeTabKey, setActiveTabKey] = useState('1');
  const [disabled, setDisabled] = useState(false);
  const [isTranslateModalVisible, setIsTranslateModalVisible] = useState(false);
  const [actionTypeIdState, setActionTypeIdState] = useState(null);
  const [actionTypeDescriptionState, setActionTypeDescriptionState] = useState(null);
  const { actionTypes } = useSelector(state => state.ticketForm);
  const { operations, parts } = useSelector(state => state.smartAction);
  const [showConfirm] = useConfirmationMessage(
    intl.formatMessage({ id: 'ticket.smart_actions.CONFIRM_SMART_ACTION_TITLE' }),
    intl.formatMessage({ id: 'ticket.smart_actions.CONFIRM_SMART_ACTION_TEXT' }),
  );

  useEffect(() => {
    const {
      params: { actionTypeId, actionId },
    } = match;
    setActionTypeIdState(actionTypeId);
    setActionTypeDescriptionState(actionTypes?.find(a => a.id === actionTypeId)?.description || '');
    if (actionId) {
      fetchSmartAction(actionId);
    }
  }, []);

  const goBack = () => {
    history.back();
  };

  const smartActionAutoClosed = ticketActionStatus[action.status] === ticketActionStatus[512];

  const gotoCreateOrder = (createAction) => {
    setActionForCreatingOrder(createAction);
    // if you close the triggered sidepanel, it will do history.go-1, be sure it has the current URL
    // eslint-disable-next-line max-len
    const redirectToActionTicket = history.location.pathname.replace(/\/smart-action(.*)/, `/smart-action/${id}/${createAction?.id}`);
    history.replace(redirectToActionTicket);

    // eslint-disable-next-line max-len
    const path = history.location.pathname.replace(/\/smart-action(.*)/, `/create-order/${context?.type}/${id}/${createAction?.id}`);
    history.push(path);
  };

  useEffect(() => {
    // show notification when action is saved and no order needs to be linked
    if (success && !success.creatingOrder) {
      notification.success({
        message: intl.formatMessage({ id: 'ticket.SAVE_SUCCESS' }),
        placement: 'top',
      });
      dispatch(resetSmartActionSuccess());
      goBack();
    }
    // dont't show a notification, but go to the wizard for creating an order
    if (success && success.creatingOrder) {
      gotoCreateOrder(success.ticketAction);
    }
  }, [success]);

  /**
   *
   * @param {bool} creatingOrder true when an action needs to be saved before creating an order
   */
  const createAction = (creatingOrder = false) => {
    const description = form.getFieldValue('orderDescription');
    const note = form.getFieldValue('note');
    const originalNote = form.getFieldValue('originalNote');
    const scope = JSON.parse(localStorage.getItem('serviceCenter'));
    const ticketId = ticket.id;

    createSmartAction({
      creatingOrder,
      actionType: actionTypeIdState,
      description,
      note: note ? sanitizeNote(note) : null,
      originalNote: originalNote ? sanitizeNote(originalNote) : null,
      scope: scope?.value,
      ticketId,
      operations,
      parts: parts?.map((part) => {
        const quantity = form.getFieldValue(part?.id);
        return {
          ...part,
          quantity,
        };
      }),
    });
  };

  const openOrder = () => {
    history.push(`/order/${action?.orderId}`, { context: action?.orderId });
  };

  const saveAction = ({ creatingOrder = false, cb = null }) => {
    const description = form.getFieldValue('orderDescription');
    const note = form.getFieldValue('note');
    const originalNote = form.getFieldValue('originalNote');
    const scope = JSON.parse(localStorage.getItem('serviceCenter'));

    saveSmartAction({
      id: match.params.actionId,
      description,
      note: note ? sanitizeNote(note) : null,
      originalNote,
      scope: scope?.value,
      ticketId: ticket.id,
      operations,
      cb,
      creatingOrder,
      parts: parts?.map((part) => {
        const quantity = form.getFieldValue(part?.id);
        return {
          ...part,
          quantity,
        };
      }),
    });
  };

  const createOrder = () => {
    if (action && Object.keys(action)?.length > 0) {
      saveAction({ creatingOrder: true });
    } else {
      // save smartAction before going to add order
      createAction(true);
    }
  };

  const assignOrder = () => {
    if (assignOrderToSmartAction) {
      saveAction({ cb: () => assignOrderToSmartAction() });
      goBack();
    }
  };

  const orderOrigin = origin === 'order';

  const confirmAction = (cb) => {
    if (
      !operations?.filter(o => o.addToOrder)?.length &&
      !parts?.filter(o => o.addToOrder)?.length
    ) {
      showConfirm(() => cb());
    } else {
      cb();
    }
  };

  const renderSaveButton = () => <>{
    action.orderId ?
      <StyledSaveButton
        onClick={goBack}
        type='secondary'
        data-test-id='btn-close-smart-action'
      >
        {intl.formatMessage({ id: 'ticket.smart_actions.CLOSE_SMART_ACTION' })}
      </StyledSaveButton> :
      <StyledSaveButton
        loading={isSaving}
        data-test-id='btn-save-smart-action'
        onClick={() =>
          match.params.actionId ? saveAction({ creatingOrder: false }) : createAction()
        }
        type='secondary'
        disabled={disabled || isLoadingAction || smartActionAutoClosed}
      >
        {intl.formatMessage({ id: 'ticket.smart_actions.SAVE_SMART_ACTION' })}
      </StyledSaveButton>
  }</>;

  const getCounter = (tab) => {
    let data = [];
    if (tab === tabs.operationsTableTab) {
      data = operations;
    } else if (tab === tabs.partTableTab) {
      data = parts;
    }
    if (data) {
      return data?.length;
    }
    return null;
  };

  return (
    <>
      <SidePanel
        width={'40%'}
        title={actionTypeDescriptionState}
        onPanelClose={goBack}
        contentOverflow={false}
      >
        <Form layout='vertical'>
          <HeaderRow>
            <Col span={18}>{context && <NavContextItem key={context.type} item={context} />}</Col>
            <StyledButtonCol span={6}>
              {action?.orderId ?
                <StyledButton
                  onClick={orderOrigin ? openTicket : openOrder}
                  type='secondary'
                  disabled={isLoadingAction}
                  data-test-id='btn-open-smart-order'
                >
                  {
                    orderOrigin ?
                      intl.formatMessage({ id: 'ticket.smart_actions.OPEN_SMART_TICKET' }) :
                      intl.formatMessage({ id: 'ticket.smart_actions.OPEN_SMART_ORDER' })
                  }
                </StyledButton> :
                <PermissionCheck permissionsList={['ordercreate']}>
                  <StyledButton
                    onClick={orderOrigin ?
                      () => confirmAction(assignOrder) :
                      () => confirmAction(createOrder)}
                    type='secondary'
                    disabled={disabled || isLoadingAction || smartActionAutoClosed}
                    data-test-id='btn-link-smart-order'
                  >
                    {
                      orderOrigin ?
                        intl.formatMessage({ id: 'ticket.smart_actions.LINK_SMART_TICKET' }) :
                        intl.formatMessage({ id: 'ticket.smart_actions.LINK_SMART_ORDER' })
                    }
                  </StyledButton>
                </PermissionCheck>
              }
            </StyledButtonCol>
          </HeaderRow>
          <Divider />
          <Row>
            <Col span={24}>
              <StyledTabs
                activeTabKey={activeTabKey}
                tabChange={tabKey => setActiveTabKey(tabKey)}
                boltTitle={true}
              >
                <StyledTabPane
                  key={1}
                  tab={intl.formatMessage({ id: 'ticket.smart_actions.GENERAL' })}
                  className='ticket-tab'
                >
                  <GeneralTab
                    form={form}
                    tags={tags}
                    setIsTranslateModalVisible={setIsTranslateModalVisible}
                    ticketMessage={ticket?.message}
                    isLoadingAction={isLoadingAction}
                    action={action}
                    actionId={match.params.actionId}
                    renderSaveButton={renderSaveButton}
                    setDisabled={setDisabled}
                    smartActionAutoClosed={smartActionAutoClosed}
                  />
                </StyledTabPane>
                {isToggleEnabled('ticketSmartActionOperationsAndParts') &&
                  <>
                    <StyledTabPane
                      key={2}
                      tab={<>{intl.formatMessage({ id: 'equipment.taskList.OPERATIONS' })}
                        <Counter>{getCounter(tabs.operationsTableTab)}</Counter></>}
                      className='ticket-tab'
                    >
                      <OperationsTableTab
                        renderSaveButton={renderSaveButton}
                        disabled={action?.status}
                      />
                    </StyledTabPane>
                    <StyledTabPane
                      key={3}
                      tab={<>{intl.formatMessage({ id: 'equipment.taskList.PARTS' })}
                        <Counter>{getCounter(tabs.partTableTab)}</Counter></>}
                      className='ticket-tab'
                    >
                      <PartTableTab
                        renderSaveButton={renderSaveButton}
                        disabled={action?.status}
                        form={form}
                      />
                    </StyledTabPane>
                  </>
                }
              </StyledTabs>
            </Col>
          </Row>
        </Form>
      </SidePanel >
      <TranslationModalContainer
        visible={isTranslateModalVisible}
        footer={null}
        onCancel={() => setIsTranslateModalVisible(false)}
        textToTranslate={form.getFieldValue('originalNote') || form.getFieldValue('note') || ''}
        setTranslatedValue={(v) => {
          const note = form.getFieldValue('note');
          form.setFieldsValue({
            originalNote: note,
            note: `${v}`,
          });
        }}
      />
    </>
  );
};

SmartActionSidePanel.propTypes = {
  id: PropTypes.string,
  match: PropTypes.object,
  history: PropTypes.object,
  intl: PropTypes.object,
  form: PropTypes.object,
  context: PropTypes.object,
  ticket: PropTypes.object,
  tags: PropTypes.array,
  saveSmartAction: PropTypes.func,
  createSmartAction: PropTypes.func,
  success: PropTypes.bool,
  isSaving: PropTypes.bool,
  action: PropTypes.object,
  fetchSmartAction: PropTypes.func,
  setActionForCreatingOrder: PropTypes.func,
  isLoadingAction: PropTypes.bool,
  assignOrderToSmartAction: PropTypes.func,
  origin: PropTypes.string,
  openTicket: PropTypes.func,
};

export default Form.create()(injectIntl(withRouter(SmartActionSidePanel)));
